// =============================================================================
// AdminPermissionsMatrix — the per-admin module permissions matrix
// =============================================================================
// Rows = every reduced-Admin account; columns = the admin modules; each cell is a
// tick-box that grants/removes that module for that admin. Saved per-row into
// profiles.admin_permissions (NULL = the role default until first edited). Gated by
// RequireAccess screen="permissions" — reachable by SuperUsers AND any Supervisor
// granted the "Zephyr Staff" module (John, 7 Sep 2026; access.ts SCREEN_ACCESS).
// ⚠️ Whoever can open this can grant themselves any other module — the SoD point of
// the model (docs/permissions-model-review.md). See docs/superuser-role-plan.md.
// =============================================================================
import React from "react";
import { ADMIN_MODULES, DEFAULT_ADMIN_PERMISSIONS } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { listStaffProfiles, updateProfileById } from "../../data/profiles";
import { logAudit } from "../../utils/auditLog";

// Friendly column labels for each admin module (matches the nav labels in
// src/app/access.ts). Keep in sync when a module's nav label changes — the
// component test asserts every ADMIN_MODULES id has a label here.
export const MODULE_LABELS = {
  reports: "Reports & Statistics",
  templates: "System Content",
  users: "DFV Services",
  staff: "Zephyr Staff",
  import: "Import",
  invites: "Invitations",
  audit: "Audit Log",
  suppliers: "Suppliers",
  schools: "Schools",
  crm: "DFV Services Communication",
  telemarketing: "Telemarketing",
  feedback: "Client Feedback",
  lookups: "Dropdown Lists",
  push_queue: "DFV Services Push List",
  products: "Products",
  product_map: "Product Mapping",
  so_rules: "SO Rules",
  sales_orders: "Sales Orders",
  top_up_product_map: "Top-up Product Mapping",
  top_up_so_rules: "Top-up SO Rules",
  top_up_tasks: "Top-up Task List",
  resource_content: "Resources Management",
  calendar_manage: "Calendar Management",
  monitored_approval: "Approve Monitored DFV Services",
};

// Columns are shown ALPHABETICALLY by label for scannability (John, 28 Jul 2026).
// Storage order is unaffected — save() still writes in ADMIN_MODULES order.
const SORTED_MODULES = [...ADMIN_MODULES].sort((a, b) =>
  (MODULE_LABELS[a] || a).localeCompare(MODULE_LABELS[b] || b)
);

// The effective module set for a profile row: its saved array, or the role
// default when admin_permissions is NULL/absent.
function effectivePerms(adminPermissions) {
  return new Set(Array.isArray(adminPermissions) ? adminPermissions : DEFAULT_ADMIN_PERMISSIONS);
}

export function AdminPermissionsMatrix() {
  const { user } = useSession();
  const [admins, setAdmins] = React.useState(null);
  const [savingId, setSavingId] = React.useState(null);
  const [savedId, setSavedId] = React.useState(null);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async function () {
    setError("");
    const rows = await listStaffProfiles();
    if (!rows) {
      setError("Could not load admin accounts.");
      setAdmins([]);
      return;
    }
    const list = rows
      .filter((r) => r.role === "admin")
      .map((r) => ({
        id: r.id,
        name: r.display_name || r.staff_user_email || r.id,
        email: r.staff_user_email || "",
        perms: effectivePerms(r.admin_permissions),
        hasCustom: Array.isArray(r.admin_permissions),
        dirty: false,
      }));
    setAdmins(list);
  }, []);

  React.useEffect(
    function () {
      load();
    },
    [load]
  );

  function toggle(adminId, moduleId) {
    setSavedId(null);
    setAdmins(function (prev) {
      return prev.map(function (a) {
        if (a.id !== adminId) return a;
        const next = new Set(a.perms);
        if (next.has(moduleId)) next.delete(moduleId);
        else next.add(moduleId);
        return Object.assign({}, a, { perms: next, dirty: true });
      });
    });
  }

  async function save(adminId) {
    const a = admins.find((x) => x.id === adminId);
    if (!a) return;
    setSavingId(adminId);
    setError("");
    // Persist in ADMIN_MODULES order so the stored array is stable + readable.
    const perms = ADMIN_MODULES.filter((m) => a.perms.has(m));
    const res = await updateProfileById(adminId, { admin_permissions: perms });
    setSavingId(null);
    if (res.error) {
      setError(`Save failed for ${a.name}: ${res.error.message}`);
      return;
    }
    logAudit("admin_permissions_changed", {
      target_profile_id: adminId,
      target_email: a.email,
      permissions: perms,
      by: user?.email || "",
    });
    setAdmins(function (prev) {
      return prev.map(function (x) {
        return x.id === adminId ? Object.assign({}, x, { dirty: false, hasCustom: true }) : x;
      });
    });
    setSavedId(adminId);
    setTimeout(function () {
      setSavedId(function (cur) {
        return cur === adminId ? null : cur;
      });
    }, 3000);
  }

  return (
    <div className="p-5">
      <h1 className="text-[22px] font-bold text-zephyr-teal-dark mt-0 mb-1">
        Supervisor Permissions
      </h1>
      <p className="text-[13px] text-zephyr-muted mt-0 mb-4 max-w-[760px]">
        Choose which modules each Supervisor can access. Tick to grant, untick to remove, then Save
        that row. Supervisors you haven't customised use the default set. SuperUsers always have
        full access and are not listed here.
      </p>

      {error && (
        <div className="bg-zephyr-red-light border border-[#EF9A9A] rounded-[6px] py-2 px-3 mb-3 text-[13px] text-zephyr-red font-bold">
          {error}
        </div>
      )}

      {admins === null ? (
        <div className="text-zephyr-muted text-[13px]">Loading...</div>
      ) : admins.length === 0 ? (
        <div className="text-zephyr-muted text-[13px]">No Supervisor accounts to configure.</div>
      ) : (
        <div className="overflow-x-auto border border-zephyr-border rounded-[10px]">
          <table className="border-collapse text-[13px]" style={{ minWidth: 900 }}>
            <thead>
              <tr className="bg-zephyr-teal-dark text-white">
                <th className="text-left font-bold py-2 px-3 sticky left-0 bg-zephyr-teal-dark z-10">
                  Supervisor
                </th>
                {SORTED_MODULES.map((m) => (
                  <th
                    key={m}
                    className="font-bold"
                    style={{ height: 150, width: 39, verticalAlign: "bottom", padding: 0 }}
                  >
                    {/* 45°-angled label anchored so its FIRST letter sits at the
                        column centre (left:50%) — i.e. right above the checkbox below —
                        which lets the 13 module columns pack tight. */}
                    <div style={{ position: "relative", height: 150 }}>
                      <div
                        className="whitespace-nowrap text-[12px]"
                        style={{
                          position: "absolute",
                          left: "50%",
                          bottom: 8,
                          transform: "rotate(-45deg)",
                          transformOrigin: "left bottom",
                        }}
                      >
                        {MODULE_LABELS[m] || m}
                      </div>
                    </div>
                  </th>
                ))}
                <th className="py-2 px-3 text-center align-bottom">Save</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a, i) => (
                <tr
                  key={a.id}
                  className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}
                  style={{ borderTop: "1px solid #ECEFF1" }}
                >
                  <td className="py-2 px-3 font-semibold text-zephyr-text sticky left-0 bg-inherit whitespace-nowrap">
                    {a.name}
                    {!a.hasCustom && (
                      <span className="ml-2 font-normal text-[11px] text-zephyr-muted">
                        (defaults)
                      </span>
                    )}
                    {/* Email dropped (John, 6 Sep 2026) — the name is enough here and the
                        sticky name column is narrower without it, giving the matrix more room. */}
                  </td>
                  {SORTED_MODULES.map((m) => (
                    <td key={m} className="py-2 px-1 text-center">
                      <input
                        type="checkbox"
                        checked={a.perms.has(m)}
                        aria-label={`${a.name}: ${MODULE_LABELS[m] || m}`}
                        onChange={function () {
                          toggle(a.id, m);
                        }}
                      />
                    </td>
                  ))}
                  <td className="py-2 px-3 text-center whitespace-nowrap">
                    <button
                      type="button"
                      onClick={function () {
                        save(a.id);
                      }}
                      disabled={savingId === a.id || !a.dirty}
                      className={[
                        "border-none rounded-[6px] py-[6px] px-4 text-[13px] font-bold",
                        savingId === a.id
                          ? "cursor-wait opacity-70 bg-zephyr-teal text-white"
                          : a.dirty
                            ? "cursor-pointer bg-zephyr-teal text-white"
                            : "cursor-default opacity-50 bg-zephyr-border text-zephyr-muted",
                      ].join(" ")}
                      style={{ fontFamily: "inherit" }}
                    >
                      {savingId === a.id ? "Saving..." : "Save"}
                    </button>
                    {savedId === a.id && (
                      <span className="ml-2 text-[12px] text-zephyr-teal font-semibold">
                        Saved ✓
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Role reference — the baseline access for each internal role, so admins can see
          "who has rights to what" at a glance (John, 27 Aug 2026). Supervisors get NO
          Manage modules by default (DEFAULT_ADMIN_PERMISSIONS is empty, John 6 Sep 2026);
          each is granted per-Supervisor in the matrix above. */}
      <div className="mt-8 max-w-[760px] text-[13px] text-zephyr-text">
        <div className="mb-5">
          <div className="font-bold mb-1">Base Supervisor permissions</div>
          <div className="text-zephyr-muted mb-2">
            A Supervisor you haven't customised (the "defaults" set) can reach:
          </div>
          <ul className="m-0 pl-5 list-disc">
            <li>
              Responders Task List and All Requests — a Supervisor also does the responder job.
            </li>
            <li>Resources — the Invoicing / Responders pages and the Calendar.</li>
            <li>
              <strong>No Manage modules by default</strong> — a SuperUser grants each module per
              Supervisor by ticking it in the matrix above, then Save that row.
            </li>
            <li>SuperUsers always have full access, regardless of the matrix.</li>
          </ul>
        </div>
        <div className="mb-5">
          <div className="font-bold mb-1">Responder permissions</div>
          <ul className="m-0 pl-5 list-disc">
            <li>Responders Task List — take on and action requests.</li>
            <li>All Requests — view and search every request.</li>
            <li>Resources — the Invoicing / Responders pages and the Calendar.</li>
            <li>No Manage / admin modules, and no per-Supervisor matrix.</li>
          </ul>
        </div>
        <div>
          <div className="font-bold mb-1">Pickers permissions</div>
          <ul className="m-0 pl-5 list-disc">
            <li>Pickers Task List — take on and action Sales Orders.</li>
            <li>All Requests — read-only (view items and comments).</li>
            <li>Resources — the Invoicing / Responders pages and the Calendar.</li>
            <li>No approvals, and no Manage / admin modules.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
